import { useCallback } from "react";
import styled from "styled-components";
import { ActionMenu, Checkbox, Icon, Stack, Title, useTheme, useToast } from "@introist/react-foundation/v2";
import { useNavigate } from "react-router-dom";
import { isEmpty, partition } from "lodash";

import { Check, StepTypeTag } from "components/atoms";

import { api, JourneyStepV2 } from "services/rpc/RpcProvider";
import { useJourneyStepActions } from "modules/journeys/hooks";
import { getJourneyStepTypeWithEventType } from "services/api/WorkflowApi";
import { List, ListRow } from "components/organisms";
import { getTitleForStepType } from "modules/workflows/components/steps";
import { RescheduleJourneyStep } from "modules/journeys/components/RescheduleJourneyStep";
import { StepStatusBadge } from "modules/journeys/components/StepStatusBadge";
import { Automation } from "../../automations";
import { TargetDateIndicator } from "../../automations/components/TargetDateIndicator";
import {
  DateFormats,
  formatIntroistDateTime,
  introistDateTimeToMoment
} from "../../../utils/dates";
import { Skeletons } from "../../../components/molecules";

type JourneyStepListProps = {
  automation: Automation;
  journeySteps?: JourneyStepV2[];
  selectedSteps?: string[];
  onSelectionChange?: (selectedSteps: string[]) => void;
};

export const JourneyStepStatusIcon = ({ status }: { status: string }) => {
  const { theme } = useTheme();
  switch (status) {
    case "completed":
      return <Check checked size={1} />;
    case "canceled":
      return <Icon name="crossCircle" color={theme.palette.foreground.subdued} />;
    case "failing":
      return <Icon name="warning" color={theme.palette.danger.default} />;
    default:
      return <Check checked={false} size={1} />;
  }
};

const StepName = styled(Title)<{ $strokeThrough: boolean }>`
  position: relative;
  text-decoration: ${({ $strokeThrough }) => ($strokeThrough ? "line-through" : "none")};
`;

const StepRow = ({
  step,
  onClick,
  isSelectable,
  isSelected,
  onToggleSelect
}: {
  step: JourneyStepV2;
  onClick: (stepId: string) => void;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (stepId: string) => void;
}) => {
  const toast = useToast();
  const apiUtils = api.useContext();

  const { refetch: reloadStep } = api.journeys.steps.find.useQuery({
    stepId: step.id
  });

  const { actions, rescheduleOpen, setRescheduleOpen } = useJourneyStepActions(step);

  const updateAll = useCallback(async () => {
    await reloadStep();
    await apiUtils.journeys.steps.list.refetch({ journeyId: step.journeyId });
    await apiUtils.automations.find.refetch({ id: step.journeyId });
  }, [reloadStep, apiUtils, step.journeyId]);

  const handleCheckboxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onToggleSelect && isSelectable) {
      onToggleSelect(step.id);
    }
  };

  return (
    <ListRow
      gridTemplateColumns={isSelectable ? "32px auto auto 150px 20px" : "auto auto 150px 20px"}
      onClick={() => onClick(step.id)}
    >
      {isSelectable && (
        <div onClick={handleCheckboxClick}>
          <Checkbox
            checked={isSelected ?? false}
            disabled={!isSelectable}
            onChange={() => {}}
          />
        </div>
      )}
      <Stack>
        <JourneyStepStatusIcon status={step.status} />
        <StepTypeTag compact stepType={getJourneyStepTypeWithEventType(step)} />
        <StepName variant="bold" $strokeThrough={step.status === "canceled"}>
          {step.title ?? getTitleForStepType(getJourneyStepTypeWithEventType(step))}
        </StepName>
      </Stack>
      <Stack justifyContent="flex-end" style={{ paddingRight: 16 }}>
        <StepStatusBadge
          status={step.status}
          scheduledAt={introistDateTimeToMoment(step.start).toISOString()}
          stepType={step.stepType}
          pendingPreconditions={step.pendingPreconditions}
        />
      </Stack>
      <Title small>{formatIntroistDateTime(step.start, DateFormats.datetime.withMonth.long)}</Title>
      <div onClick={event => event.stopPropagation()}>
        {!isEmpty(actions) && step.status !== "canceled" ? (
          <>
            <ActionMenu options={actions} />
            <RescheduleJourneyStep
              allowCreate={!step.journeyData?.doNotCreate}
              open={rescheduleOpen}
              onClose={() => setRescheduleOpen(false)}
              step={step}
              onRescheduled={async () => {
                await updateAll();
                setRescheduleOpen(false);
                toast.success("Step rescheduled!");
              }}
            />
          </>
        ) : (
          <div />
        )}
      </div>
    </ListRow>
  );
};

const TargetDateRow = styled(ListRow)`
  background-color: var(--palette-surface-dimmed);
  padding-right: 1.75rem;
`;

export const JourneyStepTable = ({
  automation,
  journeySteps,
  selectedSteps = [],
  onSelectionChange,
  ...rest
}: JourneyStepListProps) => {
  const navigate = useNavigate();

  const [beforeTarget, afterTarget] = partition(
    journeySteps,
    step => new Date(step.start.date) < new Date(automation.targetDate)
  );

  const onRowClick = (stepId: string) => {
    navigate(`steps/${stepId}`, { relative: "path", state: { from: "journey" } });
  };

  // Determine which steps are selectable (only draft status)
  const selectableSteps = journeySteps?.filter(step => step.status === "draft") ?? [];
  const selectableStepIds = new Set(selectableSteps.map(step => step.id));

  const handleToggleSelect = (stepId: string) => {
    if (!onSelectionChange) return;

    if (selectedSteps.includes(stepId)) {
      onSelectionChange(selectedSteps.filter(id => id !== stepId));
    } else {
      onSelectionChange([...selectedSteps, stepId]);
    }
  };

  const handleSelectAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!onSelectionChange) return;

    if (selectedSteps.length === selectableSteps.length) {
      // Deselect all
      onSelectionChange([]);
    } else {
      // Select all selectable steps
      onSelectionChange(selectableSteps.map(step => step.id));
    }
  };

  const showCheckboxes = onSelectionChange !== undefined;
  const allSelectableSelected =
    selectableSteps.length > 0 && selectedSteps.length === selectableSteps.length;

  if (!journeySteps) return <Skeletons.List />;

  return (
    <List {...rest}>
      {showCheckboxes && selectableSteps.length > 0 && (
        <ListRow
          gridTemplateColumns="32px auto"
          onClick={handleSelectAll}
          style={{ backgroundColor: "var(--palette-surface-dimmed)", cursor: "pointer" }}
        >
          <Checkbox
            checked={allSelectableSelected}
            onChange={() => {}}
          />
          <Title small>
            {allSelectableSelected
              ? `Deselect all (${selectableSteps.length})`
              : `Select all draft steps (${selectableSteps.length})`}
          </Title>
        </ListRow>
      )}

      {beforeTarget.map(step => (
        <StepRow
          key={step.id}
          step={step}
          onClick={onRowClick}
          isSelectable={showCheckboxes && selectableStepIds.has(step.id)}
          isSelected={selectedSteps.includes(step.id)}
          onToggleSelect={handleToggleSelect}
        />
      ))}

      <TargetDateRow gridTemplateColumns="1fr auto">
        <TargetDateIndicator targetDate={automation.targetDate} />
      </TargetDateRow>

      {afterTarget.map(step => (
        <StepRow
          key={step.id}
          step={step}
          onClick={onRowClick}
          isSelectable={showCheckboxes && selectableStepIds.has(step.id)}
          isSelected={selectedSteps.includes(step.id)}
          onToggleSelect={handleToggleSelect}
        />
      ))}
    </List>
  );
};
